import {
  CreateFavoriteMutation_Mutation,
  RemoveFavoriteProductMutation_Mutation,
} from "@/Apollo/schema";
import { ApolloCache } from "@apollo/client";

/**
 * Drop every cached page of the `favorites` query.
 *
 * Favoriting mutates a list the mutation result says nothing about: `createFavorite` returns the
 * new favorite, `removeFavorite` returns a boolean, and neither tells the cache that
 * `Query.favorites` — a paginated, sorted, counted list — is now wrong. Since `useMyFavorites`
 * runs on the default `cache-first` policy, a stale entry is served for the rest of the session:
 * the account page keeps showing an unfavorited product, and misses a newly favorited one.
 *
 * `fieldName` evicts the field for *all* argument combinations, so every cached page and sort
 * order goes, not just the one last viewed. The next read refetches. Surgically splicing the item
 * in or out would have to keep `pagination.count`, page boundaries and sort order honest across
 * every cached page — far more code to get wrong than one round trip costs.
 */
export const evictFavoritesList = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: "ROOT_QUERY", fieldName: "favorites" });
  cache.gc();
};

export const getCreateFavoriteProductOptimisticResponse = (
  productId: string,
  favoriteId: string,
  createdAt: Date
): CreateFavoriteMutation_Mutation => {
  return {
    __typename: "Mutation",
    createFavorite: {
      __typename: "Favorite",
      id: favoriteId,
      created: createdAt.toISOString(),
      item: {
        __typename: "Product",
        id: productId,
        favorite: {
          __typename: "Favorite",
          id: favoriteId,
        },
      },
    },
  };
};

export const getRemoveFavoriteProductOptimisticResponse =
  (): RemoveFavoriteProductMutation_Mutation => {
    return {
      __typename: "Mutation",
      removeFavorite: true,
    };
  };
